import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  XCircle,
  Gauge,
  Lock,
  FileText,
  Layers,
  Zap,
  ClipboardCheck,
  ShieldAlert,
} from "lucide-react";
import { Button } from "../components/ui/button";
import BreadcrumbSchema from "../components/BreadcrumbSchema";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { SecurityAuditHeroVisual } from "../components/HeroVisuals";
import { SecurityFindingsDashboardVisual } from "../components/SectionVisuals";
import {
  securityAuditMeta,
  engagementPillars,
  concurrencyCap,
  coverageIntro,
  auditLayers,
  pricingModel,
  lifecycle,
  tiers,
  tierFloorNote,
  tierMatrix,
  discoveryOutcomes,
  exampleSteps,
  exampleTotal,
  exampleNote,
  reportContents,
  paymentSchedule,
  termsAtAGlance,
  securityAuditFaqs,
} from "../data/securityAudit";

const ACCENT = "#10B981";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

function useFaqSchema(faqs, path) {
  useEffect(() => {
    if (!faqs || faqs.length === 0) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-faq-schema", "true");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [faqs, path]);
}

export default function SecurityAuditPage() {
  useFaqSchema(securityAuditFaqs, securityAuditMeta.path);

  return (
    <>
      <Seo
        title={securityAuditMeta.title}
        description={securityAuditMeta.metaDescription}
        path={securityAuditMeta.path}
        image={securityAuditMeta.ogImage || securityAuditMeta.coverImage}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "https://maxiomapps.com/" },
          { name: "Security Audits", url: `https://maxiomapps.com${securityAuditMeta.path}` },
        ]}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-72 h-72 rounded-full blur-3xl" style={{ background: `${ACCENT}0D` }} />
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
              <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>
                {securityAuditMeta.category}
              </p>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6 whitespace-pre-line" data-testid="security-audit-heading">
                {securityAuditMeta.heading}
              </h1>
              <p className="text-lg text-neutral-300 max-w-xl leading-relaxed mb-10">
                {securityAuditMeta.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid="security-cta-primary">
                  <Button
                    className="text-white font-medium px-8 py-6 text-base rounded-md transition-all duration-300"
                    style={{ backgroundColor: ACCENT, boxShadow: `0 0 20px ${ACCENT}4D` }}
                  >
                    Request an Audit <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#tiers" data-testid="security-cta-tiers">
                  <Button variant="outline" className="border-white/20 hover:border-[#10B981] text-white hover:text-[#10B981] px-8 py-6 text-base rounded-md transition-all duration-300">
                    See the Tiers <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              </div>
            </motion.div>
            <SecurityAuditHeroVisual />
          </div>
        </div>
      </section>

      {/* Trusted By Logos + Clutch */}
      <section className="py-10 bg-[#0A0A0A] border-b border-white/5" data-testid="security-trusted-by">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <p className="text-center text-xs uppercase tracking-widest text-neutral-400 mb-6">Trusted by Industry Leaders</p>
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16 opacity-50 hover:opacity-70 transition-opacity">
              {[
                { name: "Google", url: "https://www.maxiomtech.com/images/logos/logo-3-black.svg" },
                { name: "BMW", url: "https://www.maxiomtech.com/images/logos/Mask-Group-6.png" },
                { name: "Exelis", url: "https://www.maxiomtech.com/images/logos/exelis-3-LOGO.png" },
                { name: "Telus", url: "https://www.maxiomtech.com/images/logos/Mask-Group-5.png" },
              ].map((logo) => (
                <img
                  key={logo.name}
                  src={logo.url}
                  alt={`${logo.name} logo`}
                  className="h-7 md:h-9 w-auto brightness-0 invert object-contain"
                  loading="lazy"
                />
              ))}
            </div>
            <a
              href="https://clutch.co/profile/maxiom-technology"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-gray-200 hover:border-[#10B981]/60 hover:bg-[#10B981]/10 transition-colors"
              data-testid="security-clutch-badge"
            >
              <span className="flex h-5 w-20 items-center justify-center rounded bg-transparent text-[11px] font-bold text-[#F59E0B] tracking-tight leading-none">
                ★★★★★
              </span>
              <span className="font-semibold text-white">Top-rated on Clutch</span>
              <span className="text-gray-400 hidden sm:inline">Champion &amp; Global Leader</span>
            </a>
          </div>
        </div>
      </section>

      {/* How the engagement works */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="security-engagement">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>Overview</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">How the engagement works</h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {engagementPillars.map((p, i) => (
              <motion.div
                key={p.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="bg-[#121212] border border-white/5 rounded-xl p-8 hover:border-[#10B981]/30 transition-all duration-300"
                data-testid={`engagement-pillar-${i}`}
              >
                <div className="text-sm font-mono mb-4" style={{ color: ACCENT }}>0{i + 1}</div>
                <h3 className="text-lg font-semibold text-white mb-3">{p.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="rounded-xl border border-[#10B981]/20 bg-[#10B981]/5 p-8 flex flex-col sm:flex-row sm:items-center gap-6"
            data-testid="security-capacity"
          >
            <div className="flex items-baseline gap-2">
              <span className="text-4xl md:text-5xl font-bold text-white">{concurrencyCap.value}</span>
              <span className="text-gray-300 text-lg">{concurrencyCap.label}</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed sm:border-l sm:border-white/10 sm:pl-6">{concurrencyCap.note}</p>
          </motion.div>
        </div>
      </section>

      {/* What we audit: five architectural layers */}
      <section className="py-20 md:py-32 bg-[#0f0f0f] border-y border-white/5" data-testid="security-coverage">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>{coverageIntro.label}</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">{coverageIntro.heading}</h2>
            <p className="text-gray-400 text-lg leading-relaxed max-w-3xl">{coverageIntro.description}</p>
          </motion.div>

          {/* Layer overview strip */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6"
          >
            {auditLayers.map((layer) => (
              <div
                key={layer.number}
                className="bg-[#121212] border border-white/5 rounded-xl px-4 py-4 flex items-center gap-3"
                data-testid={`coverage-pill-${layer.number}`}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${ACCENT}1A` }}>
                  <layer.icon className="w-4 h-4" style={{ color: ACCENT }} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono" style={{ color: ACCENT }}>0{layer.number}</div>
                  <div className="text-white text-sm font-medium truncate">{layer.title.split(" & ")[0].split(" ")[0]}</div>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Why it matters callout */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="rounded-xl border border-[#10B981]/20 bg-[#10B981]/5 p-6 mb-12 flex items-start gap-3"
            data-testid="coverage-why"
          >
            <ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} />
            <p className="text-gray-300 text-sm leading-relaxed">{coverageIntro.whyItMatters}</p>
          </motion.div>

          {/* Layer detail blocks */}
          <div className="space-y-4">
            {auditLayers.map((layer, li) => (
              <motion.div
                key={layer.number}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={li * 0.4}
                className="bg-[#121212] border border-white/5 rounded-2xl p-6 md:p-8 hover:border-[#10B981]/30 transition-all duration-300"
                data-testid={`coverage-layer-${layer.number}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                  {/* Layer header */}
                  <div className="lg:col-span-3">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${ACCENT}1A` }}>
                        <layer.icon className="w-5 h-5" style={{ color: ACCENT }} />
                      </div>
                      <span className="text-3xl font-bold text-white/10">0{layer.number}</span>
                    </div>
                    <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: ACCENT }}>
                      Area {layer.number} of 5
                    </p>
                    <h3 className="text-xl font-bold text-white mb-2">{layer.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{layer.scope}</p>
                  </div>
                  {/* Checks */}
                  <div className="lg:col-span-9">
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                      {layer.checks.map((c, ci) => (
                        <div key={ci} className="flex items-start gap-2.5">
                          <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} />
                          <div>
                            <div className="text-white text-sm font-medium">{c.name}</div>
                            <div className="text-gray-500 text-xs leading-relaxed mt-0.5">{c.detail}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why fixed-tier, not hourly */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="security-model">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>Model</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Why fixed-tier, not hourly</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-[#121212] border border-white/5 rounded-xl p-8"
              data-testid="model-hourly"
            >
              <h3 className="text-lg font-semibold text-gray-300 mb-6">{pricingModel.hourly.label}</h3>
              <ul className="space-y-4">
                {pricingModel.hourly.points.map((pt, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-500/80 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400 text-sm leading-relaxed">{pt}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
              className="rounded-xl p-8 border"
              style={{ backgroundColor: `${ACCENT}0D`, borderColor: `${ACCENT}33` }}
              data-testid="model-fixed-tier"
            >
              <h3 className="text-lg font-semibold text-white mb-6">{pricingModel.fixedTier.label}</h3>
              <ul className="space-y-4">
                {pricingModel.fixedTier.points.map((pt, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} />
                    <span className="text-gray-200 text-sm leading-relaxed">{pt}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The audit lifecycle */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="security-lifecycle">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>Process</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">The audit lifecycle</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {lifecycle.map((step, i) => (
              <motion.div
                key={step.step}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-[#10B981]/30 transition-all duration-300 relative"
                data-testid={`lifecycle-step-${i}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${ACCENT}1A` }}>
                    <step.icon className="w-5 h-5" style={{ color: ACCENT }} />
                  </div>
                  <span className="text-2xl font-bold text-white/10">{step.step}</span>
                </div>
                <h3 className="text-base font-semibold text-white mb-1">{step.title}</h3>
                <p className="text-[11px] uppercase tracking-wider mb-3" style={{ color: ACCENT }}>{step.timing}</p>
                <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Five audit tiers */}
      <section id="tiers" className="py-20 md:py-32 bg-[#0f0f0f] border-y border-white/5 scroll-mt-24" data-testid="security-tiers">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>Pricing</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Five audit tiers</h2>
            <p className="text-gray-400 text-lg max-w-3xl">Fixed per audit, set by technical complexity. {tierFloorNote}</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {tiers.map((t, i) => (
              <motion.div
                key={t.tier}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="bg-[#121212] border border-white/5 rounded-xl p-6 text-center hover:border-[#10B981]/40 transition-all duration-300"
                data-testid={`tier-${i}`}
              >
                <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">{t.tier}</div>
                <div className="text-3xl font-bold text-white mb-3">{t.price}</div>
                <div className="h-px w-10 mx-auto mb-3" style={{ backgroundColor: `${ACCENT}66` }} />
                <p className="text-gray-400 text-sm leading-relaxed">{t.summary}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How we assign a tier: matrix */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="security-matrix">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>Pricing</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">How we assign a tier</h2>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="overflow-x-auto rounded-xl border border-white/5">
            <table className="w-full min-w-[760px] border-collapse text-sm">
              <thead>
                <tr className="bg-[#121212]">
                  <th className="text-left text-gray-300 font-semibold px-4 py-4 border-b border-white/5">Component</th>
                  {tierMatrix.columns.map((c, i) => (
                    <th key={c} className="text-left font-semibold px-4 py-4 border-b border-white/5" style={i === tierMatrix.columns.length - 1 ? { color: ACCENT } : { color: "#d1d5db" }}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tierMatrix.rows.map((row, ri) => (
                  <tr key={row.component} className={ri % 2 === 0 ? "bg-[#0f0f0f]" : "bg-[#0c0c0c]"}>
                    <td className="text-white font-medium px-4 py-3.5 border-b border-white/5 align-top">{row.component}</td>
                    {row.values.map((v, vi) => (
                      <td key={vi} className="text-gray-400 px-4 py-3.5 border-b border-white/5 align-top">{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
          <p className="text-gray-500 text-sm mt-5 flex items-start gap-2">
            <Layers className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} />
            <span>Read rule: {tierFloorNote}</span>
          </p>
        </div>
      </section>

      {/* Discovery deep-dive */}
      <section className="py-20 md:py-32 bg-[#0f0f0f] border-y border-white/5" data-testid="security-discovery">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>Step 1 · Discovery</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">Discovery (72 hours)</h2>
              <p className="text-gray-400 text-lg leading-relaxed">
                Discovery determines the Audit Tier. The 50% audit fee is due after this phase; the audit begins when it's paid, so you always know the price before you commit.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="space-y-4">
              {discoveryOutcomes.map((o, i) => (
                <div key={i} className="bg-[#121212] border border-white/5 rounded-xl p-6" data-testid={`discovery-outcome-${i}`}>
                  <h3 className="text-white font-semibold mb-2">{o.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{o.desc}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Priority fee + example */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="security-priority-example">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Priority fee */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-[#121212] border border-white/5 rounded-xl p-8">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-6" style={{ backgroundColor: `${ACCENT}1A` }}>
                <Zap className="w-6 h-6" style={{ color: ACCENT }} />
              </div>
              <p className="text-sm uppercase tracking-widest font-medium mb-3" style={{ color: ACCENT }}>Optional · Speed</p>
              <h3 className="text-2xl font-bold text-white mb-4">Priority / acceleration fee</h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                Accelerate an audit by up to 25%. The fee is proportionate to the acceleration you choose, applied to the audit's base (tier) cost.
              </p>
              <div className="rounded-lg border border-white/5 bg-[#0f0f0f] px-5 py-4 flex items-center justify-between">
                <span className="text-gray-300 text-sm">Tier 2 ($10K), delivered 25% faster</span>
                <span className="font-bold" style={{ color: ACCENT }}>+ $2,500</span>
              </div>
            </motion.div>

            {/* Example */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="bg-[#121212] border border-white/5 rounded-xl p-8">
              <p className="text-sm uppercase tracking-widest font-medium mb-3" style={{ color: ACCENT }}>Example</p>
              <h3 className="text-2xl font-bold text-white mb-6">End-to-end example</h3>
              <div className="space-y-3">
                {exampleSteps.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
                    <span className="text-gray-400 text-sm">{s.label}</span>
                    <span className="text-white text-sm font-medium whitespace-nowrap">{s.value}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 pt-2">
                  <span className="text-white font-semibold">Total</span>
                  <span className="text-2xl font-bold" style={{ color: ACCENT }}>{exampleTotal}</span>
                </div>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed mt-5">{exampleNote}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The preliminary report */}
      <section className="py-20 md:py-32 bg-[#0f0f0f] border-y border-white/5" data-testid="security-report">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>Step 3 · Delivery</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">The preliminary report</h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                Delivered on completion. A prioritized, evidence-backed report your engineers can act on immediately. It contains:
              </p>
              <ul className="space-y-4">
                {reportContents.map((item, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`report-item-${i}`}>
                    <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} />
                    <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <SecurityFindingsDashboardVisual />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Review + Additional audit + Closeout */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="security-review-closeout">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Review */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-[#121212] border border-white/5 rounded-xl p-8">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-6" style={{ backgroundColor: `${ACCENT}1A` }}>
                <Gauge className="w-6 h-6" style={{ color: ACCENT }} />
              </div>
              <p className="text-sm uppercase tracking-widest font-medium mb-3" style={{ color: ACCENT }}>Step 4 · Review</p>
              <h3 className="text-xl font-bold text-white mb-4">Review window</h3>
              <ul className="space-y-3 text-sm text-gray-400 leading-relaxed">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} /> 2 weeks included (questions, clarifications, walkthroughs).</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} /> Not new audit work or fix verification.</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} /> After 2 weeks: $5,000 / week.</li>
              </ul>
            </motion.div>

            {/* Additional audit */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="bg-[#121212] border border-white/5 rounded-xl p-8">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-6" style={{ backgroundColor: `${ACCENT}1A` }}>
                <Layers className="w-6 h-6" style={{ color: ACCENT }} />
              </div>
              <p className="text-sm uppercase tracking-widest font-medium mb-3" style={{ color: ACCENT }}>If needed</p>
              <h3 className="text-xl font-bold text-white mb-4">Additional audit</h3>
              <ul className="space-y-3 text-sm text-gray-400 leading-relaxed">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} /> Re-audit to verify fixes, or an expanded scope.</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} /> Quoted as a fixed fee.</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} /> Same model: 50% up front, 50% on delivery.</li>
              </ul>
            </motion.div>

            {/* Closeout */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} className="bg-[#121212] border border-white/5 rounded-xl p-8">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-6" style={{ backgroundColor: `${ACCENT}1A` }}>
                <ClipboardCheck className="w-6 h-6" style={{ color: ACCENT }} />
              </div>
              <p className="text-sm uppercase tracking-widest font-medium mb-3" style={{ color: ACCENT }}>Step 5 · Closeout</p>
              <h3 className="text-xl font-bold text-white mb-4">Closeout &amp; completion</h3>
              <ul className="space-y-3 text-sm text-gray-400 leading-relaxed">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} /> The project closes on mutual agreement.</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} /> We issue a Statement of Completion for the work performed.</li>
                <li className="flex items-start gap-2"><Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} /> It attests to work performed, not a warranty that the system is secure.</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Payment schedule */}
      <section className="py-20 md:py-32 bg-[#0f0f0f] border-y border-white/5" data-testid="security-payment-schedule">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>Terms</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Payment schedule</h2>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="overflow-x-auto rounded-xl border border-white/5">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="bg-[#121212]">
                  <th className="text-left text-gray-300 font-semibold px-5 py-4 border-b border-white/5">Milestone</th>
                  <th className="text-left text-gray-300 font-semibold px-5 py-4 border-b border-white/5">Timing</th>
                  <th className="text-left text-gray-300 font-semibold px-5 py-4 border-b border-white/5">Amount</th>
                </tr>
              </thead>
              <tbody>
                {paymentSchedule.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-[#0f0f0f]" : "bg-[#0c0c0c]"}>
                    <td className="text-white font-medium px-5 py-4 border-b border-white/5">{row.milestone}</td>
                    <td className="text-gray-400 px-5 py-4 border-b border-white/5">{row.timing}</td>
                    <td className="px-5 py-4 border-b border-white/5 font-medium" style={{ color: ACCENT }}>{row.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Terms at a glance */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="security-terms">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>Summary</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Terms at a glance</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {termsAtAGlance.map((t, i) => (
              <motion.div
                key={t.label}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="bg-[#121212] border border-white/5 rounded-xl p-6"
                data-testid={`term-${i}`}
              >
                <div className="text-xs uppercase tracking-widest mb-3" style={{ color: ACCENT }}>{t.label}</div>
                <p className="text-gray-300 text-sm leading-relaxed">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-32 bg-[#0f0f0f]" data-testid="security-faq">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="lg:col-span-4">
              <p className="text-sm uppercase tracking-widest font-medium mb-4" style={{ color: ACCENT }}>FAQ</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">Frequently Asked Questions</h2>
              <p className="text-gray-400 leading-relaxed">
                Have a codebase you want audited? Tell us the surface area and we'll scope a tier.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 font-medium mt-6 hover:gap-3 transition-all" style={{ color: ACCENT }} data-testid="security-faq-contact-link">
                Get in Touch <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="lg:col-span-8">
              <Accordion type="single" collapsible className="space-y-3">
                {securityAuditFaqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="bg-[#121212] border border-white/5 rounded-xl px-6 data-[state=open]:border-[#10B981]/30 transition-colors"
                    data-testid={`security-faq-item-${i}`}
                  >
                    <AccordionTrigger className="text-white text-left font-medium py-5 hover:text-[#10B981] hover:no-underline [&[data-state=open]]:text-[#10B981]">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-400 text-sm leading-relaxed pb-5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="security-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">Ready to scope your security audit?</h2>
            <p className="text-gray-400 text-lg mb-10">
              Submit a target and we'll determine the tier in 72 hours. A known, fixed price before any audit work begins.
            </p>
            <Link to="/contact" data-testid="security-bottom-cta">
              <Button
                className="text-white font-medium px-8 py-6 text-base rounded-md transition-all duration-300"
                style={{ backgroundColor: ACCENT, boxShadow: `0 0 20px ${ACCENT}4D` }}
              >
                Request an Audit <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
